import React from "react";
import { format, isAfter, isToday, parseISO } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { CalendarDays, User, MapPin, Truck, ArrowRight } from "lucide-react";
import api from "../../api";
import { toast } from "react-toastify";

const UpcomingMissions = ({ missions, onEdit }) => {
  const { t, i18n } = useTranslation();

  const localeMap = { fr, en: enUS };
  const currentLocale = localeMap[i18n.language] || enUS;

  const perPage = 5;
  const [page, setPage] = React.useState(1);

  const upcomingList = React.useMemo(() => {
    const now = new Date();
    return (missions || [])
      .filter((m) => {
        const date = parseISO(m.date);
        return isToday(date) || isAfter(date, now);
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [missions]);

  const pageCount = Math.max(1, Math.ceil(upcomingList.length / perPage));
  const visible = upcomingList.slice((page - 1) * perPage, page * perPage);

  const handleGenerateWaybill = async (missionId) => {
    try {
      const response = await api.get(`/waybill/${missionId}`, {
        responseType: "blob",
      });
      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank", "noopener,noreferrer");
      toast.success(t("mission.list.waybillSuccess"));
    } catch (err) {
      toast.error(t("mission.list.waybillError"));
      console.error(err);
    }
  };

  if (visible.length === 0) {
    return (
      <div className="text-center text-base-content/50 text-lg py-20">
        🎉 {t("mission.upcoming.empty")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {visible.map((m) => {
        const d = parseISO(m.date);
        const shortDate = format(d, d.getFullYear() === new Date().getFullYear() ? "dd-MM" : "dd-MM-yyyy", { locale: currentLocale });
        const typeLabel = m.type === "Moving" ? t("mission.moving") : t("mission.delivery");
        const hasRoute = m.departure && m.arrival;

        return (
          <div
            key={m.id}
            onClick={() => onEdit(m)}
            className="cursor-pointer bg-base-100 rounded-xl border border-base-200 shadow-sm p-4 hover:bg-base-200/40 hover:border-primary/30 transition-colors"
          >
            {/* Date + statut */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CalendarDays size={15} className="text-primary shrink-0" />
                <span className="text-sm font-bold text-base-content capitalize">{shortDate}</span>
              </div>
              <span
                className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                  m.paid ? "bg-success/10 text-success" : "bg-error/10 text-error"
                }`}
              >
                {m.paid ? t("mission.status.paid") : t("mission.status.unpaid")}
              </span>
            </div>

            {/* Trajet départ → arrivée */}
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={15} className="text-error shrink-0" />
              {hasRoute ? (
                <span className="flex items-center gap-1.5 text-[15px] font-bold text-base-content">
                  {m.departure}
                  <ArrowRight size={15} className="text-base-content/40" />
                  {m.arrival}
                </span>
              ) : (
                <span className="text-sm font-medium text-base-content/80 truncate">
                  {m.address || "N/A"}
                </span>
              )}
            </div>

            {/* Type + client */}
            <div className="flex items-center gap-2 mb-3">
              <Truck size={15} className="text-error shrink-0" />
              <span className="text-sm font-semibold text-base-content">{typeLabel}</span>
              {m.client && (
                <span className="flex items-center gap-1 text-xs text-base-content/50 ml-1">
                  <User size={12} /> {m.client}
                </span>
              )}
              {m.external_source === "desbrasenplus" && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/10 text-orange-600 border border-orange-500/20"
                  title={m.external_id ? `Des Bras en Plus · #${m.external_id}` : "Importé de Des Bras en Plus"}
                >
                  DBEP
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-primary">{m.priceTTC} €</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateWaybill(m.id);
                }}
                className="btn btn-ghost btn-xs flex items-center gap-1.5 text-primary hover:bg-primary/10"
              >
                <Truck size={13} /> {t("mission.list.waybill")}
              </button>
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex justify-center mt-6 gap-2">
          {Array.from({ length: pageCount }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`btn btn-sm ${
                page === i + 1 ? "btn-primary" : "btn-ghost text-base-content/70"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingMissions;
